import React, { useState } from 'react';
import { signIn, signUp } from 'aws-amplify/auth';
import { useAuth } from '../AuthContext';
import { toE164 } from '../phone';
import Hint from './Hint';
import ForgotPassword from './ForgotPassword';
import { PASSWORD_RULES } from '../passwordRules';

type Mode = 'login' | 'register' | 'forgot';

const SHOW_PHONE_HELP =
  "Yes means other approved attendees can see your phone number on the Who's Coming page, so people can coordinate rides and tents. Organizers can always see it either way, and you can change this any time under My Registration.";

const OFFER_TENT_HELP =
  "Say Yes if you have room in your tent for someone who needs a spot. Other approved attendees will see a badge on the Who's Coming page so they know who to ask.";

const OFFER_RIDE_HELP =
  "Say Yes if you have room in your vehicle and could pick someone up on the way. Other approved attendees will see a badge on the Who's Coming page so they know who to ask.";

/**
 * Sign-up is held for approval: the user pool has auto-verification turned off,
 * so a new account sits in UNCONFIRMED status and cannot sign in until an admin
 * confirms it. Both the post-register screen and the UserNotConfirmedException
 * on login funnel into the same "pending" message.
 *
 * The trip registration questions from the old Google Form are collected here as
 * Cognito attributes. These names must match the user pool exactly (they cannot
 * be renamed once created) and must be writable by the app client:
 *   custom:full.name, custom:additional.attendies, custom:bringing,
 *   custom:share.tent, custom:need.help.travel, custom:dietary.restrictions
 */
export default function Auth({ initialMode = 'login' }: { initialMode?: Mode }): JSX.Element {
  const { refresh, configured } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [additional, setAdditional] = useState('0');
  const [bringing, setBringing] = useState('');
  const [tentShare, setTentShare] = useState('No');
  const [needsRide, setNeedsRide] = useState('No');
  const [hasDietary, setHasDietary] = useState('No');
  const [dietaryNotes, setDietaryNotes] = useState('');
  const [showPhone, setShowPhone] = useState('No');
  const [offersTent, setOffersTent] = useState('No');
  const [offersRide, setOffersRide] = useState('No');

  const [error, setError] = useState('');
  // '' = not pending; otherwise how they got here, which changes the wording.
  const [pending, setPending] = useState<'' | 'registered' | 'login'>('');
  const [busy, setBusy] = useState(false);

  if (!configured) {
    return (
      <div className="card">
        <h2>🔒 Login Not Set Up Yet</h2>
        <p>
          This page will be password protected once the Cognito user pool is configured.
          Add <strong>REACT_APP_USER_POOL_ID</strong> and{' '}
          <strong>REACT_APP_USER_POOL_CLIENT_ID</strong> to <code>.env</code>, then restart
          the dev server.
        </p>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="card">
        <h2>⏳ Awaiting Approval</h2>
        <p>
          {pending === 'registered'
            ? "Thanks for registering! You're on the list. An organizer still has to approve your account before you can sign in."
            : "Your account exists and your password is correct — it just hasn't been approved yet. An organizer reviews each registration before it can sign in."}
        </p>
        <p>
          <strong>An admin will text you once you're approved.</strong> After that, come
          back and log in with the same email and password — signing in shows the exact
          campground, site number, and directions.
        </p>
        <div className="form-actions">
          <button
            type="button"
            onClick={() => {
              setPending('');
              setMode('login');
              setPassword('');
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Enter both an email and a password.');
      return;
    }

    setBusy(true);
    try {
      if (mode === 'register') {
        if (!fullName.trim()) {
          setError('Enter your full name.');
          return;
        }

        const phoneNumber = toE164(phone);
        if (!phoneNumber) {
          setError('Enter a valid phone number, for example (423) 555-0123.');
          return;
        }

        if (!tentShare || !needsRide || !hasDietary) {
          setError('Please answer all of the Yes/No questions.');
          return;
        }

        if (hasDietary === 'Yes' && !dietaryNotes.trim()) {
          setError('Please list your dietary restrictions or allergies.');
          return;
        }

        const userAttributes: Record<string, string> = {
          email,
          phone_number: phoneNumber,
          'custom:full.name': fullName.trim(),
          'custom:additional.attend.ex': additional,
          'custom:share.tent': tentShare,
          'custom:show.phone.number': showPhone,
          'custom:open.to.sharing': offersTent,
          'custom:open.to.giving.ride': offersRide,
          'custom:need.help.travel': needsRide,
          // One attribute holds the answer: the specifics, or "None".
          'custom:dietary.restrictions': hasDietary === 'Yes' ? dietaryNotes.trim() : 'None',
        };

        // Cognito rejects empty attribute values, so only send optional ones when filled.
        if (bringing.trim()) userAttributes['custom:bringing'] = bringing.trim();

        await signUp({ username: email, password, options: { userAttributes } });
        setPending('registered');
      } else {
        // Amplify resolves rather than throwing for an unapproved account: it
        // returns isSignedIn false with a CONFIRM_SIGN_UP next step. Without
        // handling that, clicking Login appears to do nothing at all.
        const { isSignedIn, nextStep } = await signIn({ username: email, password });

        if (isSignedIn) {
          await refresh();
        } else if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          setPending('login');
        } else {
          setError('Could not sign you in. Please try again.');
        }
      }
    } catch (err) {
      // An unapproved account is the expected case, not an error - explain it
      // properly instead of showing a one-line failure.
      if ((err as { name?: string })?.name === 'UserNotConfirmedException') {
        setPending('login');
      } else {
        setError(messageFor(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'forgot') {
    return (
      <ForgotPassword
        initialEmail={email}
        onBackToLogin={() => {
          setMode('login');
          setError('');
          setPassword('');
        }}
      />
    );
  }

  const registering = mode === 'register';

  return (
    <div className="card auth-card">
      <h2>{registering ? '📋 Register for the Camping Trip' : '🔒 Login'}</h2>
      <p>
        {registering
          ? 'Register your attendance and create your login at the same time. An organizer will approve your account before you can sign in.'
          : 'Sign in to see the campsite location, directions, and who else is coming.'}
      </p>

      <form onSubmit={handleSubmit}>
        {registering && (
          <label>
            Full Name
            <input
              autoComplete="name"
              placeholder="First and Last"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
          </label>
        )}

        <label>
          Email
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </label>

        {registering && (
          <label>
            Phone Number
            <input
              type="tel"
              autoComplete="tel"
              placeholder="(423) 555-0123"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </label>
        )}

        {registering && (
          <label>
            <span className="label-row">
              Show my phone number to other attendees?
              <Hint label={SHOW_PHONE_HELP}>{SHOW_PHONE_HELP}</Hint>
            </span>
            <select value={showPhone} onChange={e => setShowPhone(e.target.value)}>
              <option value="No">No — keep it private</option>
              <option value="Yes">Yes — show it on Who's Coming</option>
            </select>
          </label>
        )}

        <label>
          <span className="label-row">
            Password
            {registering && (
              <Hint label={`Password must contain: ${PASSWORD_RULES.join(', ')}`}>
                <strong>Password must contain:</strong>
                <ul>
                  {PASSWORD_RULES.map(rule => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </Hint>
            )}
          </span>
          <input
            type="password"
            autoComplete={registering ? 'new-password' : 'current-password'}
            title={registering ? `Must contain: ${PASSWORD_RULES.join(', ')}` : undefined}
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </label>

        {registering && (
          <>
            <label>
              How many additional people are you bringing? (Children)
              <select value={additional} onChange={e => setAdditional(e.target.value)}>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </select>
            </label>

            <label>
              What are you bringing for the group? (Games or food — optional)
              <input value={bringing} onChange={e => setBringing(e.target.value)} />
            </label>

            <label>
              Do you need to share a tent with someone?
              <select value={tentShare} onChange={e => setTentShare(e.target.value)}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label>
              Do you need help getting to the campsite?
              <select value={needsRide} onChange={e => setNeedsRide(e.target.value)}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label>
              <span className="label-row">
                Do you have room in your tent for someone?
                <Hint label={OFFER_TENT_HELP}>{OFFER_TENT_HELP}</Hint>
              </span>
              <select value={offersTent} onChange={e => setOffersTent(e.target.value)}>
                <option value="No">No</option>
                <option value="Yes">Yes — I can share my tent</option>
              </select>
            </label>

            <label>
              <span className="label-row">
                Could you give someone a ride?
                <Hint label={OFFER_RIDE_HELP}>{OFFER_RIDE_HELP}</Hint>
              </span>
              <select value={offersRide} onChange={e => setOffersRide(e.target.value)}>
                <option value="No">No</option>
                <option value="Yes">Yes — I can give a ride</option>
              </select>
            </label>

            <label>
              Any dietary restrictions or food allergies?
              <select value={hasDietary} onChange={e => setHasDietary(e.target.value)}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            {hasDietary === 'Yes' && (
              <label>
                Please specify (e.g. Vegan, Gluten-Free, Nut Allergy)
                <input value={dietaryNotes} onChange={e => setDietaryNotes(e.target.value)} />
              </label>
            )}
          </>
        )}

        {error && <p className="error">{error}</p>}

        <div className="form-actions">
          <button type="submit" disabled={busy}>
            {busy ? 'Please wait…' : registering ? 'Register' : 'Login'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setMode(registering ? 'login' : 'register');
              setError('');
            }}
          >
            {registering ? 'Back to Login' : 'Need an account?'}
          </button>
          {!registering && (
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                setMode('forgot');
                setError('');
              }}
            >
              Forgot password?
            </button>
          )}
        </div>
      </form>
    </div>
  );
}


function messageFor(err: unknown): string {
  const name = (err as { name?: string })?.name;
  const message = (err as { message?: string })?.message || '';

  switch (name) {
    case 'UserNotConfirmedException':
      return 'Your account is still waiting on approval from an organizer.';
    case 'NotAuthorizedException':
      return message.includes('attributes')
        ? 'Signup is missing attribute permissions in Cognito. An organizer needs to update the app client settings.'
        : 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'No account found for that email.';
    case 'UsernameExistsException':
      return 'An account already exists for that email.';
    case 'InvalidParameterException':
      return message.includes('phone')
        ? 'That phone number was not accepted. Use a format like (423) 555-0123.'
        : message || 'Something went wrong. Please try again.';
    case 'InvalidPasswordException':
      return 'That password does not meet the minimum requirements.';
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      return message || 'Something went wrong. Please try again.';
  }
}
